import { isNil, join, map, pipe } from 'ramda';
import { useMemo } from 'react';
import {
  mediaManagerAddToBoardFinished,
  mediaManagerAddToBoardMenuClick,
  mediaManagerBoardAction,
  mediaManagerBoardClick,
  mediaManagerBoardPopUpClick,
  mediaManagerBoardSaved,
  mediaManagerCreateBoardClick,
  mediaManagerDeleteBoardPopUpClick,
} from '@wix/bi-logger-media-data/v2';
import { MediaManagerSDKBoardSitesVisibility } from '../../../constants/BoardSitesVisibility';
import { MediaManagerSDKBoardTeamAvailability } from '../../../constants/BoardsTeamAvailability';
import { bi } from '../../../services/loggers/bi';
import { extractCommonBiFieldsFromItems, TFieldsToPick } from '../../bi';
import { getId } from '../../common';
import { useMMGRSettings } from '../../hoc/withMMGRSettings';
import { MediaManagerSDKAnyItem, MediaManagerSDKBoard } from '../../media-manager-sdk';
import { useFileType } from './useFileType';
import { ActionClickOrigin } from './useTrackActions/useTrackActions.types';

export enum CreateBoardPopUpTarget {
  Create = 'create',
  Cancel = 'cancel',
  Close = 'close',
  Cover = 'cover',
  Edit = 'edit',
}

export enum TrackBoardActionsMenuTarget {
  Create = 'create',
  Edit = 'edit',
  AddFiles = 'add_files',
  Delete = 'delete',
  Manage = 'manage',
}

export enum TrackAddToBoardPopUpTarget {
  Add = 'add',
  New = 'new',
}

export enum TrackBoardActionsMenuClickOrigin {
  InfoPanel = 'more_info',
  Menu = 'menu',
  EmptyState = 'empty_state',
  Board = 'board',
  TeammatesList = 'teammates_list',
}

export enum TrackBoardClickOrigin {
  InfoPanel = 'more_info',
  File = 'file',
  BoardsList = 'boards_list',
  ItemContextMenu = 'item_context_menu',
  TopBar = 'top_bar',
}

interface ITrackBoardActionsClick {
  target: TrackBoardActionsMenuTarget;
  clickOrigin: TrackBoardActionsMenuClickOrigin;
}

export enum TrackAddToBoardFinishedTarget {
  Add = 'add',
  Remove = 'remove',
}

export enum TrackDeleteBoardPopUpClickTarget {
  Delete = 'delete',
  Cancel = 'cancel',
  Close = 'close',
}

interface ITrackAddToBoardFinished {
  item: MediaManagerSDKAnyItem | MediaManagerSDKAnyItem[];
  target: TrackAddToBoardFinishedTarget;
  clickOrigin: ActionClickOrigin;
}

interface ITrackBoardSaved {
  boardId: string;
  availability: MediaManagerSDKBoardSitesVisibility;
  teamAvailability: MediaManagerSDKBoardTeamAvailability;
  coverAdded: boolean;
  isNew: boolean;
  clickOrigin: ActionClickOrigin;
}

interface ITrackDeleteBoardPopUpClick {
  boards: MediaManagerSDKBoard[];
  target: TrackDeleteBoardPopUpClickTarget;
}

interface ITrackAddToBoardMenuClick {
  target: TrackAddToBoardPopUpTarget;
  clickOrigin: ActionClickOrigin;
}

export enum TrackBoardClickTarget {
  Add = 'add',
  Remove = 'remove',
}

export interface ITrackBoardClick {
  target: TrackBoardClickTarget;
  clickOrigin: ActionClickOrigin;
}

export const extractBoardsIds: (boards: MediaManagerSDKBoard[]) => string = pipe(map(getId), join(','));

const commonBiFieldsToPick: TFieldsToPick = [
  'fileType',
  'fileFormat',
  'fileId',
  'mediaType',
  'name',
  'mediaOrigin',
  'fileOrigin',
];

export const useTrackBoardAnalytics = () => {
  const fileType = useFileType();
  const {
    mmgrSettings: { team },
  } = useMMGRSettings();

  const teamInfo = isNil(team) ? {} : { teammate_id: window.userId };

  return useMemo(
    () => ({
      trackCreateBoardPopUpClick: (target: CreateBoardPopUpTarget) =>
        bi.logger().report(mediaManagerBoardPopUpClick({ target })),
      trackBoardActionsClick: (boards: MediaManagerSDKBoard[], { target, clickOrigin }: ITrackBoardActionsClick) =>
        bi.logger().report(
          mediaManagerBoardAction({
            target,
            clickOrigin,
            boardId: extractBoardsIds(boards),
            ...teamInfo,
          }),
        ),

      trackCreateBoardClick: (clickOrigin: TrackBoardClickOrigin) =>
        bi.logger().report(mediaManagerCreateBoardClick({ clickOrigin, ...teamInfo })),

      trackAddToBoardFinished: (
        boards: MediaManagerSDKBoard[],
        { item, target, clickOrigin }: ITrackAddToBoardFinished,
      ) =>
        bi.logger().report(
          mediaManagerAddToBoardFinished({
            boardId: extractBoardsIds(boards),
            target,
            clickOrigin,
            ...extractCommonBiFieldsFromItems(item, { fileType }, commonBiFieldsToPick),
          }),
        ),
      trackBoardSaved: ({
        boardId,
        availability,
        coverAdded,
        isNew,
        clickOrigin,
        teamAvailability,
      }: ITrackBoardSaved) =>
        bi.logger().report(
          mediaManagerBoardSaved({
            boardId,
            availability,
            teamAvailability,
            coverAdded,
            isNew,
            clickOrigin,
          }),
        ),
      trackDeleteBoardPopUpClick: ({ boards, target }: ITrackDeleteBoardPopUpClick) =>
        bi.logger().report(
          mediaManagerDeleteBoardPopUpClick({
            boardId: extractBoardsIds(boards),
            target,
          }),
        ),
      trackBoardClick: (
        items: MediaManagerSDKAnyItem | MediaManagerSDKAnyItem[],
        { target, clickOrigin }: ITrackBoardClick,
      ) =>
        bi.logger().report(
          mediaManagerBoardClick({
            target,
            clickOrigin,
            ...extractCommonBiFieldsFromItems(items, { fileType }, commonBiFieldsToPick),
          }),
        ),

      trackAddToBoardMenuClick: (items: MediaManagerSDKAnyItem[], { target, clickOrigin }: ITrackAddToBoardMenuClick) =>
        bi.logger().report(
          mediaManagerAddToBoardMenuClick({
            target,
            clickOrigin,
            ...teamInfo,
            ...extractCommonBiFieldsFromItems(items, { fileType }, commonBiFieldsToPick),
          }),
        ),
    }),
    [fileType],
  );
};
